import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useLazyGetAdminIntakeAgreementQuery,
  useLazyGetIntakeAgreementQuery,
  useRefreshAdminIntakeAgreementMutation,
  useRefreshIntakeAgreementMutation,
} from '../store/api';
import type { DocusignAgreementStatusResponse } from '../types';

type AgreementRead =
  | ReturnType<ReturnType<typeof useLazyGetIntakeAgreementQuery>[0]>
  | ReturnType<ReturnType<typeof useLazyGetAdminIntakeAgreementQuery>[0]>;

/** A forced query may join another identity's pending void-key request. Never consume that result. */
async function freshRead(
  start: () => AgreementRead,
  signal: AbortSignal,
): Promise<DocusignAgreementStatusResponse> {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (signal.aborted) throw new Error('Agreement request cancelled');
    const request = start();
    const abort = (): void => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    try {
      const result = await request;
      if (signal.aborted) throw new Error('Agreement request cancelled');
      if (result.requestId !== request.requestId) continue;
      if (result.error || !result.data) throw new Error('Agreement status unavailable');
      return result.data;
    } finally {
      signal.removeEventListener('abort', abort);
      request.unsubscribe();
    }
  }
  throw new Error('Fresh agreement status unavailable');
}

interface AgreementState {
  scopeKey: string;
  status?: DocusignAgreementStatusResponse;
  pending: boolean;
  failed: boolean;
}

export interface IntakeAgreementState {
  status?: DocusignAgreementStatusResponse;
  pending: boolean;
  failed: boolean;
  refresh: () => Promise<void>;
  recheck: () => Promise<void>;
}

/** Local UI state only: shared query cache values cannot grant agreement access. */
function useScopedAgreement({
  organizationId,
  scopeKey,
  enabled,
  read,
  reload,
}: {
  organizationId: string;
  scopeKey: string;
  enabled: boolean;
  read: (signal: AbortSignal) => Promise<DocusignAgreementStatusResponse>;
  reload: (signal: AbortSignal) => Promise<DocusignAgreementStatusResponse>;
}): IntakeAgreementState {
  const [state, setState] = useState<AgreementState>();
  const currentScope = useRef(scopeKey);
  currentScope.current = scopeKey;
  const sequence = useRef(0);
  const controller = useRef<AbortController | undefined>(undefined);
  const pendingRefresh = useRef<Promise<void> | undefined>(undefined);
  const run = useCallback(
    async (refresh: boolean): Promise<void> => {
      if (!enabled || !organizationId) return;
      const requestSequence = ++sequence.current;
      controller.current?.abort();
      const active = new AbortController();
      controller.current = active;
      setState(previous => ({
        scopeKey,
        status: previous?.scopeKey === scopeKey && !previous.failed ? previous.status : undefined,
        pending: true,
        failed: false,
      }));
      try {
        const status = await (refresh ? reload : read)(active.signal);
        if (
          active.signal.aborted ||
          currentScope.current !== scopeKey ||
          sequence.current !== requestSequence
        )
          return;
        if (status.organization_id !== organizationId) throw new Error('Agreement scope mismatch');
        setState({ scopeKey, status, pending: false, failed: false });
      } catch {
        if (
          !active.signal.aborted &&
          currentScope.current === scopeKey &&
          sequence.current === requestSequence
        ) {
          setState({ scopeKey, pending: false, failed: true });
        }
      }
    },
    [enabled, organizationId, scopeKey, read, reload],
  );
  useEffect(() => {
    const requests = sequence;
    if (!enabled) setState(undefined);
    void run(false);
    return () => {
      requests.current++;
      controller.current?.abort();
      pendingRefresh.current = undefined;
    };
  }, [run, enabled]);
  const refresh = useCallback((): Promise<void> => {
    if (pendingRefresh.current) return pendingRefresh.current;
    const operation = run(true).finally(() => {
      if (pendingRefresh.current === operation) pendingRefresh.current = undefined;
    });
    pendingRefresh.current = operation;
    return operation;
  }, [run]);
  const current = enabled && state?.scopeKey === scopeKey ? state : undefined;
  const recheck = useCallback(() => run(false), [run]);
  return {
    status: current?.status,
    pending: enabled && (!current || current.pending),
    failed: current?.failed ?? false,
    refresh,
    recheck,
  };
}

export function useCustomerIntakeAgreement(
  organizationId: string,
  scopeKey: string,
  enabled: boolean,
): IntakeAgreementState {
  const [getStatus] = useLazyGetIntakeAgreementQuery();
  const [refreshStatus] = useRefreshIntakeAgreementMutation();
  const read = useCallback(
    (signal: AbortSignal) => freshRead(() => getStatus(undefined, false), signal),
    [getStatus],
  );
  const reload = useCallback(
    async (signal: AbortSignal): Promise<DocusignAgreementStatusResponse> => {
      const request = refreshStatus();
      const abort = (): void => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      try {
        return await request.unwrap();
      } finally {
        signal.removeEventListener('abort', abort);
        request.reset();
      }
    },
    [refreshStatus],
  );
  return useScopedAgreement({ organizationId, scopeKey, enabled, read, reload });
}

export function useAdminIntakeAgreement(
  organizationId: string,
  scopeKey: string,
  enabled: boolean,
): IntakeAgreementState {
  const [getStatus] = useLazyGetAdminIntakeAgreementQuery();
  const [refreshStatus] = useRefreshAdminIntakeAgreementMutation();
  const read = useCallback(
    (signal: AbortSignal) => freshRead(() => getStatus({ organizationId }, false), signal),
    [getStatus, organizationId],
  );
  const reload = useCallback(
    async (signal: AbortSignal): Promise<DocusignAgreementStatusResponse> => {
      const request = refreshStatus({ organizationId });
      const abort = (): void => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      try {
        return await request.unwrap();
      } finally {
        signal.removeEventListener('abort', abort);
        request.reset();
      }
    },
    [refreshStatus, organizationId],
  );
  return useScopedAgreement({ organizationId, scopeKey, enabled, read, reload });
}

export function intakeAgreementWaitingMessage(practiceAdministrator: boolean): string {
  return practiceAdministrator
    ? 'Another practice administrator is handling your agreements. You can continue your intake while they sign.'
    : 'Your practice administrator needs to sign the practice agreements. You can continue your intake in the meantime.';
}

/** Auth0 identity/role claims are used only to reset local state, never to authorize signing. */
export function intakeAgreementScope(
  organizationId: string,
  subject: string,
  authOrganization: unknown,
  roles: unknown,
): string {
  const roleScope = Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string').sort()
    : [];
  return JSON.stringify([
    organizationId,
    subject,
    typeof authOrganization === 'string' ? authOrganization : '',
    roleScope,
  ]);
}
